/** Loading / error / empty states for server data (UIUX Specification: "states"). */
import { Banner, Button, EmptyState, Spinner } from '@uatp/ui';
import { SearchX } from 'lucide-react';
import type { ReactNode } from 'react';
import { ApiError } from '../api/client';
import { errorMessage } from '../lib/query';

export function PageSpinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="grid min-h-[40vh] place-items-center" role="status" aria-live="polite">
      <div className="flex items-center gap-2 text-13 text-text-2">
        <Spinner size={16} />
        {label}
      </div>
    </div>
  );
}

/** Inline error for a failed query, with retry. 404s render a "not found" state. */
export function QueryError({ error, retry, what = 'this page' }: { error: unknown; retry?: () => void; what?: string }) {
  if (error instanceof ApiError && error.status === 404) {
    return (
      <EmptyState
        icon={<SearchX />}
        title="Not found"
        description={`We couldn’t find ${what}. It may have been deleted, or you may not have access to it.`}
        bordered
      />
    );
  }
  return (
    <Banner variant="error" title={`Couldn’t load ${what}`} action={retry ? { label: 'Retry', onClick: retry } : undefined}>
      {errorMessage(error)}
    </Banner>
  );
}

/** Renders `children(data)` once loaded; handles pending and error states consistently. */
export function Loaded<T>({
  query,
  children,
  what,
  fallback,
}: {
  query: { data: T | undefined; error: unknown; isPending: boolean; refetch: () => unknown };
  children: (data: T) => ReactNode;
  what?: string;
  fallback?: ReactNode;
}) {
  if (query.isPending) return <>{fallback ?? <PageSpinner />}</>;
  if (query.error || query.data === undefined) return <QueryError error={query.error} retry={() => void query.refetch()} what={what} />;
  return <>{children(query.data)}</>;
}

export function RetryButton({ onClick }: { onClick: () => void }) {
  return (
    <Button size="sm" variant="secondary" onClick={onClick}>
      Retry
    </Button>
  );
}
